import React from "react";

import { Button, Form, Tab, Tabs } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import ContentSettings from "./components/ContentSettings";
import DateSettings from "./components/DateSettings";
import useFormSubmitHandler from "./hooks/useFormSubmitHandler";

import { store } from "../../..";
import {
    globalActions,
    selectNotice,
} from "../../../features/global/globalSlice";
import { useAppSelector } from "../../../hooks/reduxHooks";
import { ISiteSettingsForm } from "../../../types/forms";
import SiteNotice from "./components/SiteNotice";

export const loader = async () => {
    const { settings, notices } = store.getState().global;
    const promises = [];
    !notices.length &&
        promises.push(store.dispatch(globalActions.getNotices()));
    !settings.length &&
        promises.push(store.dispatch(globalActions.getSettings()));

    await Promise.all(promises);
    return null;
};

const SiteSettings = (): React.JSX.Element | undefined => {
    const settings = useAppSelector(state => state.global.settings);
    const notice = useAppSelector(selectNotice);
    const settingsToShow = {
        content: {
            homePage: [
                "umphub_upper_section_content",
                "umphub_lower_section_content",
            ],
            registration: ["umphub_registration_policy_procedures"],
        },
        date: ["umphub_current_league_year"],
    };

    const methods = useForm<ISiteSettingsForm>({
        mode: "onChange",
        defaultValues: {
            date: {
                ...settingsToShow.date.reduce(
                    (acc, settingName) => {
                        const setting = settings.find(
                            s => s.settingName === settingName,
                        );
                        if (setting) {
                            if (setting.options) {
                                acc[settingName] = {
                                    value: setting.options.indexOf(
                                        setting.setting,
                                    ),
                                    label: setting.setting,
                                };
                            } else {
                                acc[settingName] = setting.setting;
                            }
                        }
                        return acc;
                    },
                    {} as Record<string, any>,
                ),
            },
            content: Object.keys(settingsToShow.content).reduce(
                (acc, page) => {
                    const settingValues =
                        settingsToShow.content[
                            page as keyof typeof settingsToShow.content
                        ];
                    return settingValues.reduce((subAcc, settingName) => {
                        const setting = settings.find(
                            s => s.settingName === settingName,
                        );
                        if (setting) {
                            subAcc[settingName] = setting.setting;
                        }
                        return subAcc;
                    }, acc);
                },
                {} as Record<string, any>,
            ),
            siteNotice: notice?.notice,
        },
    });

    const { onSubmit } = useFormSubmitHandler(methods);

    return (
        <section className="siteSettingsContent">
            <FormProvider {...methods}>
                <Form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Tabs
                        defaultActiveKey="date"
                        id="siteSettings"
                        data-testid="siteSettingsContent"
                    >
                        <Tab eventKey="date" title="Date Settings">
                            <DateSettings
                                settingsToShow={settingsToShow.date}
                            />
                        </Tab>
                        <Tab eventKey="notice" title="Site Notice">
                            <SiteNotice />
                        </Tab>
                        <Tab eventKey="homepage" title="Home Page">
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={settingsToShow.content.homePage}
                            />
                        </Tab>
                        <Tab eventKey="registration" title="Registration">
                            <ContentSettings
                                settings={settings.filter(
                                    s => s.category === "content",
                                )}
                                category="content"
                                settingsToShow={
                                    settingsToShow.content.registration
                                }
                            />
                        </Tab>
                    </Tabs>
                    <Button variant="primary" type="submit">
                        Update Settings
                    </Button>
                </Form>
            </FormProvider>
        </section>
    );
};

export default SiteSettings;
